import type { FacetTableData } from "./facet-table-helpers";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function cellInlineStyle(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (raw.startsWith("+")) {
    return "color:#059669;font-weight:600;font-variant-numeric:tabular-nums;";
  }
  if (raw.startsWith("-")) {
    return "color:#e11d48;font-weight:600;font-variant-numeric:tabular-nums;";
  }
  return "color:#1e293b;font-variant-numeric:tabular-nums;";
}

const TH =
  "background:#eef2ff;padding:14px 20px;border-left:1px solid #e2e8f0;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.06em;color:#334155;text-align:left;vertical-align:top;word-wrap:break-word;max-width:320px;";
const TD_BORDER = "border-top:1px solid #e2e8f0;border-left:1px solid #e2e8f0;";
const TD_BASE =
  "padding:14px 20px;vertical-align:top;word-wrap:break-word;max-width:320px;";
const ROW_HEADER = "background:#f1f5f9;font-weight:600;color:#334155;";

export function buildFacetTableHtmlFragment(table: FacetTableData): string {
  const headerCells = table.headers
    .map((h, idx) => {
      const label = String(h ?? "").trim() || `Column ${idx + 1}`;
      const left = idx > 0 ? "" : "border-left:none;";
      return `<th style="${TH}${left}">${escapeHtml(label)}</th>`;
    })
    .join("");

  const bodyRows = table.rows
    .map((row, rowIdx) => {
      const rowBg = rowIdx % 2 === 0 ? "#ffffff" : "#f8fafc";
      const cells = table.headers
        .map((header, colIdx) => {
          const val = String(row[header] ?? "");
          const left = colIdx > 0 ? TD_BORDER : "";
          if (colIdx === 0) {
            return `<td style="${TD_BASE}${left}background:#f1f5f9;${ROW_HEADER}">${escapeHtml(
              val
            )}</td>`;
          }
          return `<td style="${TD_BASE}${left}background:${rowBg};${cellInlineStyle(
            row[header]
          )}">${escapeHtml(val)}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return (
    `<table style="border-collapse:collapse;width:100%;font-family:system-ui,-apple-system,sans-serif;font-size:13px;">` +
    `<thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`
  );
}

export function buildFacetTableTsv(table: FacetTableData): string {
  const esc = (s: string) => {
    const t = s.replace(/\r|\n/g, " ");
    return /[\t"]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const headerLine = table.headers.map((h) => esc(String(h ?? ""))).join("\t");
  const lines = table.rows.map((row) =>
    table.headers.map((h) => esc(String(row[h] ?? ""))).join("\t")
  );
  return [headerLine, ...lines].join("\r\n");
}

export async function copyFacetTableRich(table: FacetTableData): Promise<void> {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${buildFacetTableHtmlFragment(table)}</body></html>`;
  const plain = buildFacetTableTsv(table);

  if (typeof navigator !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      /* fall through */
    }
  }
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(plain);
    return;
  }
  throw new Error("Clipboard not available");
}
